/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { UniversalTrackerValuePlain } from '../../types/universalTrackerValue';
import { SessionStorage } from '../../services/SessionStorage';
import { ScopeQuestionGroupOptionalValue, ScopeQuestionOptionalValue } from '@g17eco/types/survey';
import { TableColumn, UtrValueType } from '../../types/universalTracker';
import { hasSimpleNumericColumnType, isNumericValueType } from '../../utils/universalTracker';
import { isInputOverridableColumn } from '@features/question-configuration/utils';
import { UtrvStatus } from '@constants/status';
import { getFullName } from '@utils/user';
import { SurveyDelegationUser } from '@g17eco/types/user';
import { InitiativeUniversalTracker } from '@g17eco/types/initiativeUniversalTracker';
import { BulkActionUtr } from '@g17eco/types/survey-question-list';
import { UtrvConfigCode, UtrvConfigValue } from '@features/question-configuration';

export interface HighlightQuestion {
  questionIndex: number;
  groupId: string;
  rowRef: React.RefObject<HTMLDivElement>;
}

export const useHighlightQuestion = ({
  surveyGroups,
}: {
  surveyGroups: ScopeQuestionGroupOptionalValue[];
}): Omit<HighlightQuestion, 'rowRef'> | undefined => {
  const sessionIndex = SessionStorage.getQuestionIndex();
  if (!sessionIndex) {
    return;
  }

  const questionIndex = parseInt(sessionIndex);
  let total = 0;
  for (const [ix, surveyGroup] of surveyGroups.entries()) {
    const totalQuestionCount = total + getTotalQuestionCount(surveyGroup);

    const isHighlightedInGroup = questionIndex >= total && questionIndex < totalQuestionCount;
    if (isHighlightedInGroup) {
      return {
        questionIndex,
        groupId: getQuestionGroupId(ix),
      };
    }
    total = totalQuestionCount;
  }

  return;
};

export const getQuestionGroupId = (index: number) => `questionGroup-${index}`;

export const getTotalQuestionCount = (surveyGroup: ScopeQuestionGroupOptionalValue) => {
  return surveyGroup.subGroups
    ? surveyGroup.subGroups.reduce((acc, subGroup) => {
        return subGroup.list ? acc + subGroup.list.length : acc;
      }, 0)
    : surveyGroup.list.length;
};

export const getNumericSelectedQuestions = (selectedQuestions: BulkActionUtr[] = []) => {
  return selectedQuestions.filter((question) => {
    switch (question.valueType) {
      case UtrValueType.Table:
        return hasSimpleNumericColumnType(question);
      default:
        return isNumericValueType(question.valueType as UtrValueType);
    }
  });
};

const hasInputOverridableColumn = (question: Pick<BulkActionUtr, 'valueValidation'>) => {
  return question.valueValidation?.table?.columns?.some((col: TableColumn) => isInputOverridableColumn(col));
};

export const getInputOverridableSelectedQuestions = (selectedQuestions: BulkActionUtr[] = []) => {
  return selectedQuestions.filter((question) => {
    switch (question.valueType) {
      case UtrValueType.Table:
        return hasInputOverridableColumn(question);
      default:
        return isNumericValueType(question.valueType as UtrValueType);
    }
  });
};

export const countVerifiedQuestion = (listOfQuestions: ScopeQuestionOptionalValue[]) => {
  const verifiedQuestions = listOfQuestions.filter(q => q.utrv?.status === UtrvStatus.Verified);
  return verifiedQuestions.length;
};

interface UserNameListParams {
  users?: SurveyDelegationUser[];
  delegationUserIds?: string[];
  check: (user: SurveyDelegationUser) => boolean | undefined
}

export const getUserNameList = ({ users = [], delegationUserIds = [], check }: UserNameListParams): string[] => {

  if (users.length === 0 && delegationUserIds.length === 0) {
    return [];
  }

  if (users.length === 0) {
    return delegationUserIds;
  }

  const validUsers = users.filter(check);

  if (delegationUserIds.length === 0) {
    return validUsers.map(u => getFullName(u));
  }

  const ids = new Set(delegationUserIds);

  const userList: string[] = [];

  for (const user of validUsers) {
    userList.push(getFullName(user));
    ids.delete(user._id);
  }

  // Push remaining ids to the front
  for (const id of ids) {
    const user = users.find(u => u._id === id);
    userList.unshift(user ? getFullName(user) : id);
  }

  return userList;
}

const isPropertyRequired = (overriddenValue: UtrvConfigValue | undefined, isRequiredAtUtrvLevel: boolean) => {
  switch(overriddenValue) {
    case UtrvConfigValue.Optional:
      return false;
    case UtrvConfigValue.Required:
      return true;
    case UtrvConfigValue.Default:
    default:
      return isRequiredAtUtrvLevel;
  }
}

export const checkIsFieldRequired = ({
  initiativeUtr,
  utrv,
  field,
}: {
  initiativeUtr: InitiativeUniversalTracker | undefined;
  field: UtrvConfigCode;
  utrv: Pick<UniversalTrackerValuePlain, 'evidenceRequired' | 'verificationRequired' | 'noteRequired' | 'isPrivate'>;
}) => {
  const overriddenValue = initiativeUtr?.utrvConfig?.[field];
  const isRequiredOnUtrv = !!utrv[field];

  return isPropertyRequired(overriddenValue, isRequiredOnUtrv);
};
